import { Component, OnInit, OnDestroy, HostListener, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { Idle, DocumentInterruptSource, StorageInterruptSource } from '@ng-idle/core';
import { SecurityService } from '@core/Security/security.service';
import { EventsEmitterService } from '@core/Abstract/emitter.service';
import { LogService } from '@core/Abstract/log.service';
import { ConfigService } from '@core/Abstract/config.service';
import { VisualsService } from '@core/Abstract/visuals.service';
import { AuthService } from '../../../../Core/Auth/auth.service';

@Component({
    selector: 'app-timer',
    template: `
    <ol class="container-fluid">
            <!-- Logout Timer -->
            <div *ngIf="timerActive" class="timer float-md-right"> 
                <div class="inner animating" [ngClass]="{animating: resetTimerAnimation}">        
                    <div class="bg-circle"></div>        
                    <div class="counter">{{timer}}</div>        
                    <div class="pie spinner" [style.animation]="spinnerAnimation"></div>        
                    <div class="pie filler" [style.animation]="fillerAnimation"></div>        
                    <div class="mask" [style.animation]="maskAnimation"></div>    
                </div>
            </div>
        </ol>
  `
})
export class TimerComponent implements OnInit, OnDestroy, OnChanges {

    @Input() timerSeconds: number;
    @Input() timeFormat: string = "";
    @Input() reset: boolean = false;
    @Input() interrupts: boolean = true;
    @Input() logoutWhenTimesUp: boolean = true;
    @Output() timersHasReset = new EventEmitter<boolean>();

    private CUSTOM_INTERRUPTSOURCE: any[] = [new DocumentInterruptSource(
        'keydown mousedown'), new StorageInterruptSource()];

    protected resetTimerAnimation: boolean = true;
    protected timer: string;
    protected timerActive: boolean;
    spinnerAnimation:string;
    fillerAnimation:string;
    maskAnimation:string;

    constructor(private securityService: SecurityService,
        private eventsEmitterService: EventsEmitterService,
        private logger: LogService,
        public authService: AuthService,
        private configService: ConfigService,
        private visualsService: VisualsService,
        private idle: Idle) {
    }

    ngOnInit(): void {
        const countDown = this.timerSeconds ?? this.configService.getTimerSeconds();
        this.drawTimer(countDown);
     }

    ngOnDestroy() {
        // Will clear when component is destroyed e.g. route is navigated away from.
        this.idle.stop();
        this.idle.onIdleStart.observers.length = 0;
        this.idle.onIdleEnd.observers.length = 0;
        this.idle.onTimeoutWarning.observers.length = 0;
        this.idle.onTimeout.observers.length = 0;
        this.idle.onInterrupt.observers.length = 0;
        this.idle.setInterrupts([]);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if(changes['reset'] && changes['reset'].currentValue === true){
            this.resetTimer();
            this.timersHasReset.emit(true);
        }
    }

    @HostListener('window:visibilitychange', ['$event'])
    visibilitychange() {
        let totalCountdown;
        if(this.timer.includes(':')){
            let mins: number = +this.timer.split(':')[0] * 60;
            let secs: number = +this.timer.split(':')[1];
            totalCountdown= mins + secs;
        } else {
            totalCountdown = this.timer;
        }
        
        this.drawTimer(totalCountdown);
    }

    protected drawTimer(countdownSeconds: number) {
        // disable Timer from config file during tests
        this.timerActive = this.configService.getTimer();
        if (this.timerActive) {
            // sets an idle timeout of 1 seconds, for testing purposes.
            this.idle.setIdle(1);
            // sets a timeout period of 5 seconds. after 10 minutes of inactivity, the user will be considered timed out.
            this.idle.setTimeout(countdownSeconds);
            // sets the custom interrupts, in this case, things like clicks, scrolls, touches to the document
            if(this.interrupts){
                this.idle.setInterrupts(this.CUSTOM_INTERRUPTSOURCE);
            }

            this.idle.onInterrupt.subscribe(() => {
                this.resetTimerAnimation = false;
                this.updateTimerAnimation(countdownSeconds);
            });

            this.idle.onTimeoutWarning.subscribe((countdown: number) => {
                this.resetTimerAnimation = true;
                this.timer = this.formatTime(countdown);
            });

            this.idle.onTimeout.subscribe(() => {
                if(this.timeFormat === "seconds"){
                    this.timer = this.formatTime(0);
                }
                if(this.logoutWhenTimesUp){
                    this.authService.logout(true);
                }
            });

            this.idle.watch();
            this.updateTimerAnimation(countdownSeconds);
        }
    }

    private resetTimer(){
        this.resetTimerAnimation = true;
        const countDown = this.timerSeconds ?? this.configService.getTimerSeconds();
        this.timer = this.formatTime(countDown);
        this.idle.watch();
        this.updateTimerAnimation(countDown);
    }

    protected formatTime(time: number): string {
        if(this.timeFormat === "seconds"){
            return time.toString();
        }
        let minutes = Math.floor(time / 60);
        let seconds = time - minutes * 60;
        let finalTime = this.str_pad_left(minutes, '0', 2) + ':' + this.str_pad_left(seconds, '0', 2);
        return finalTime;
    }

    protected str_pad_left(string, pad, length) {
        return (new Array(length + 1).join(pad) + string).slice(-length);
    }

    protected updateTimerAnimation(countdown){
        let animationtime = countdown + 1;
        let uniqueKeyframe = `${Date.now()}`; // Unique keyframe name

        // Create new keyframe dynamically
        let styleSheet = document.styleSheets[0];
        styleSheet.insertRule(`
            @keyframes rota_${uniqueKeyframe} {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `, styleSheet.cssRules.length);

        styleSheet.insertRule(`
            @keyframes opa_${uniqueKeyframe} {
                0% { opacity: 1; }
                50%, 100% { opacity: 0; }
            }
        `, styleSheet.cssRules.length);
        styleSheet.insertRule(`
            @keyframes opareverse_${uniqueKeyframe} {
                0% { opacity: 0; }
                50%, 100% { opacity: 1; }
            }
        `, styleSheet.cssRules.length);

        this.spinnerAnimation = `rota_${uniqueKeyframe} ${animationtime}s linear forwards`;
        this.fillerAnimation = `opareverse_${uniqueKeyframe} ${animationtime}s steps(1, end)`;
        this.maskAnimation = `opa_${uniqueKeyframe} ${animationtime}s steps(1, end) forwards`;
    }
}
